<template>
  <div id="app" :data-brand="brand.key" class="min-h-screen bg-miro-warm relative" style="overflow-x: clip;">
    <!-- Background Elements - Miro Style -->
    <div class="absolute inset-0 bg-noise opacity-3"></div>
    <div class="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-primary-400/8 to-accent-400/8 rounded-full blur-3xl"></div>
    <div class="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-brand-400/8 to-secondary-400/8 rounded-full blur-3xl"></div>
    <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-primary-100/5 to-transparent rounded-full blur-3xl"></div>
    
    <div class="relative z-10 flex flex-col min-h-screen">
      <AppNavigation v-if="showNavigation" />
      <GlobalNotifications v-if="showNavigation" />

      <main :class="mainClasses" class="flex-1">
        <!-- Wait for brand-store bootstrap to settle before mounting the route.
             Without this guard the route component would mount once in the
             non-WL shell (initial render), then immediately UN-mount and
             RE-mount inside the WL shell once bootstrap resolves — firing
             onMounted twice. That double-mount was triggering two POSTs to
             /confirm-payment from OrderConfirmationView (two OrderNumbers,
             two confirmation emails). brandLoaded is set synchronously in
             the canonical-host short-circuit, so canonical Zaveato pages
             still render on the first paint. -->
        <template v-if="brandLoaded">
          <!-- White-label fast path: wrap every route in the WL shell so auth,
               order lookup, cart, etc. inherit the shop's branding instead of
               rendering bare. Shop routes are passed through here as well —
               StorefrontLayout detects WL mode and skips its own chrome so the
               two shells don't stack. -->
          <WlEngineDispatcher v-if="isWhiteLabel" />
          <template v-else>
            <div v-if="showNavigation && !noAppContainer" class="app-container">
              <router-view />
            </div>
            <router-view v-else />
          </template>
        </template>
      </main>

      <!-- Footer -->
      <AppFooter v-if="showNavigation" />

      <!-- Global Components -->
      <Toast />
      <ConfirmDialog />

      <!-- Global Error Banner - Shows errors on current page instead of redirecting -->
      <GlobalErrorBanner />

      <!-- Cookie Consent Banner (GDPR Compliance) -->
      <CookieConsentBanner />

    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import AppNavigation from './components/AppNavigation.vue'
import AppFooter from './components/AppFooter.vue'
import GlobalNotifications from './components/GlobalNotifications.vue'
import GlobalErrorBanner from './components/common/GlobalErrorBanner.vue'
import CookieConsentBanner from './components/CookieConsentBanner.vue'
import { useBaseSeo } from '@/composables/useSeo'
import { initBranding, useBranding } from '@/composables/useBranding'
import { useBrandThemeEffect } from '@/composables/useBrandThemeEffect'
import { useWlTokens } from '@/composables/useWlTokens'
import { useShopBrand } from '@/composables/useShopBrand'
import { useSettingsStore } from '@/stores/settings'
import { useCartStore } from '@/stores/cart'
import WlEngineDispatcher from '@/layouts/WlEngineDispatcher.vue'

const route = useRoute()
const settingsStore = useSettingsStore()
const cartStore = useCartStore()

// Get brand for data attribute
const { brand } = useBranding()

// Per-host shop brand — drives white-label chrome hiding.
// brandLoaded gates the router-view render so the route component mounts
// exactly ONCE (after the async bootstrap settles), not first in the
// non-WL shell and then again in the WL shell. That double-mount was
// causing OrderConfirmationView to POST /confirm-payment twice on the
// Stripe redirect — issuing two OrderNumbers + double-sending emails.
const { isWhiteLabel, isLoaded: brandLoaded } = useShopBrand()

// Inject <meta name="robots" content="noindex, nofollow"> on staging/dev/demo
// so Google never indexes non-production content.
//
// Gate on the BUILD-TIME env variable, not the runtime hostname. The previous
// hostname-based version fired during prerender.mjs — Puppeteer visits
// http://localhost:PORT to capture each route, so window.location.hostname
// is always "localhost" during prerender, which baked noindex into the
// canonical production SPA and blocked Google indexing of every zaveato.co.uk
// page. VITE_ENVIRONMENT is set by the deploy workflow at build time
// (production / staging), so the check evaluates once at build and is stripped
// entirely from the production bundle by dead-code elimination.
if (import.meta.env.VITE_ENVIRONMENT !== 'production' && typeof document !== 'undefined') {
  const existing = document.querySelector('meta[name="robots"][data-source="test-env-guard"]')
  if (!existing) {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    meta.setAttribute('data-source', 'test-env-guard')
    document.head.appendChild(meta)
  }
}

// Initialize base SEO schemas (Organization + WebSite JSON-LD)
useBaseSeo()

// Watch the resolved shop brand and mirror it onto document (CSS vars, favicon,
// title, font). No-op on the canonical Zaveato host where brand stays null.
useBrandThemeEffect().install()

// Wider WL token surface — stamps --wl-* CSS variables so every WL component
// consumes brand pack tokens (Phase 2) OR the legacy brand fields (Phase 1
// bridge) via a single unified surface. theme.css's --hf-* fallback chain
// reads --wl-* first, so existing WL views inherit pack tokens automatically
// without needing to be rewritten. Same "only touch DOM on WL hosts" guarantee.
useWlTokens().install()

// Initialize branding on app mount. Per-entity currency is now read at each
// view via `formatMoney(amount, entity.currency)` from `@/utils/formatMoney`
// (the old admin-default-currency singleton was deleted). Admin Settings page
// loads its own default via `useAdminDefaultCurrency`.
onMounted(async () => {
  // Initialize branding (sets document title, meta description, data attribute)
  initBranding()

  // Load public settings (feature flags etc.)
  settingsStore.fetchSettings()

  // Hydrate cart from the server using localStorage-persisted tokens.
  //
  // Historically this was called only inside StorefrontLayout's onMounted,
  // which meant:
  //   - White-label routes (WhiteLabelShopHome, etc.) only fire it after the
  //     shop-resolve API call settles, because the layout is v-if'd on
  //     `isWhiteLabelHost && shop`. On refresh the mini-cart badge and drawer
  //     render "empty" for a beat, and any page that renders the cart before
  //     the layout mounts sees an empty state.
  //   - Non-storefront pages (canonical Zaveato routes without the layout)
  //     never hydrate at all — an admin who added items on the storefront
  //     and switched tabs would see an empty basket.
  //
  // Firing it here runs it once per app load, before any route mounts.
  // The store is idempotent (no tokens in localStorage = fast return), so
  // this is safe on admin/provider pages too. Fire-and-forget: we don't
  // block prerender or currency load on it, because the drawer's fetch
  // spinner already handles the UX for the ~200ms it takes.
  cartStore.initializeCart().catch((err) => {
    console.warn('[App] Cart hydration failed on mount:', err)
  })

  // NOTE: `render-complete` used to fire here as a prerender signal, but it
  // fired on App.vue mount — long before route components (ServiceListingView,
  // ShopsListView, etc.) finished their data fetches. The pre-rendered HTML
  // captured by Puppeteer had only the SPA shell → Google flagged /services
  // as Soft 404. Individual data-driven views now dispatch
  // `route-content-ready` after their data settles; static views rely on the
  // prerender heuristic fallback (content-length or 15 s timeout).
})

// Hide navigation on auth pages and storefront routes (they have their own header via StorefrontLayout)
const showNavigation = computed(() => {
  // White-label custom domains never show Zaveato nav, regardless of route.
  // The shop's storefront layout owns the entire chrome on those hosts.
  if (isWhiteLabel.value) return false

  if (route.meta.hideNavigation) return false

  // Hide AppNavigation on storefront routes (they use StorefrontLayout with its own header)
  const storefrontRoutes = ['/', '/shops']
  if (storefrontRoutes.includes(route.path) || route.path.startsWith('/shop/')) {
    return false
  }
  // Services listing + category landing pages use StorefrontLayout — hide AppNavigation for those only
  if (route.name === 'Services' || (typeof route.name === 'string' && route.name.startsWith('ServiceCategory_'))) {
    return false
  }

  return true
})

// Service detail + booking pages need AppNavigation but manage their own layout
// (they have a fixed floating hero section) — skip app-container for them
const noAppContainer = computed(() => {
  const name = route.name as string | undefined
  return name === 'ServiceDetail' || name === 'ServiceBooking'
})

// Adjust main content classes based on navigation visibility
const mainClasses = computed(() => {
  if (showNavigation.value) {
    return 'pt-16' // Add padding for fixed header (64px)
  }
  return ''
})

</script>

<style>
/* Global styles */
html {
  font-family: 'Inter', sans-serif;
  font-size: 16px; /* Base font size */
}

body {
  @apply text-neutral-900;
  background: linear-gradient(135deg, #F5F3F0 0%, #fffef0 100%);
  background-attachment: fixed;
  /* clip (not hidden) prevents horizontal scroll WITHOUT creating a
     scroll-container — required so descendant `position: sticky` (e.g.
     landing page header) actually sticks against the viewport instead
     of getting trapped inside the overflow container. */
  overflow-x: clip;
}

/* Responsive container */
.app-container {
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
}

/* Mobile First - Base styles (320px and up) */
@media (min-width: 320px) {
  .app-container {
    padding: 0.75rem;
  }
}

/* Small Mobile (480px and up) */
@media (min-width: 480px) {
  .app-container {
    padding: 1rem;
  }
}

/* Tablet Portrait (768px and up) */
@media (min-width: 768px) {
  .app-container {
    max-width: 768px;
    padding: 1.5rem;
  }
}

/* Tablet Landscape / Small Desktop (1024px and up) */
@media (min-width: 1024px) {
  .app-container {
    max-width: 1024px;
    padding: 2rem;
  }
}

/* Desktop (1280px and up) */
@media (min-width: 1280px) {
  .app-container {
    max-width: 1280px;
    padding: 2rem;
  }
}

/* Large Desktop (1536px and up) */
@media (min-width: 1536px) {
  .app-container {
    max-width: 1536px;
    padding: 2.5rem;
  }
}

/* Extra Large Desktop (1920px and up) */
@media (min-width: 1920px) {
  .app-container {
    max-width: 1800px;
    padding: 3rem;
  }
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  @apply bg-accent-100/50;
}

::-webkit-scrollbar-thumb {
  @apply bg-primary-400 rounded-lg;
}

::-webkit-scrollbar-thumb:hover {
  @apply bg-primary-500;
}

/* PrimeVue overrides */
.p-button {
  @apply font-semibold rounded-2xl transition-all duration-300 shadow-miro-soft;
}

.p-button:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px -5px rgba(242, 202, 2, 0.15);
}

.p-inputtext {
  @apply transition-all duration-300 rounded-xl border-neutral-200 focus:border-primary-400 shadow-miro-soft;
}

.p-inputtext:enabled:focus {
  @apply ring-4 border-primary-500;
  --tw-ring-color: rgba(var(--brand-accent-rgb, 245, 158, 11), 0.2);
  transform: translateY(-1px);
}

.p-card {
  @apply rounded-2xl border-neutral-100/60 shadow-miro-soft bg-white/80 backdrop-blur-sm;
}

.p-card:hover {
  @apply shadow-miro-medium;
  transform: translateY(-1px);
}

.p-dropdown {
  @apply rounded-xl border-neutral-200 transition-all duration-300 shadow-miro-soft;
}

.p-dropdown:not(.p-disabled):hover {
  @apply border-neutral-300;
  transform: translateY(-1px);
}

.p-dropdown:not(.p-disabled).p-focus {
  @apply ring-4 border-primary-500;
  --tw-ring-color: rgba(var(--brand-accent-rgb, 245, 158, 11), 0.2);
}

/* Toast positioning */
.Vue-Toastification__container {
  z-index: 9999;
}

.Vue-Toastification__toast {
  @apply rounded-2xl shadow-miro-large backdrop-blur-sm;
}

.Vue-Toastification__toast--success {
  @apply bg-gradient-to-r from-success-500 to-brand-500;
}

.Vue-Toastification__toast--error {
  @apply bg-gradient-to-r from-error-500 to-error-600;
}

.Vue-Toastification__toast--warning {
  @apply bg-gradient-to-r from-warning-500 to-primary-500;
}

.Vue-Toastification__toast--info {
  @apply bg-gradient-to-r from-info-500 to-secondary-500;
}

/* Loading animations */
@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.loading-shimmer {
  background: linear-gradient(90deg, #f0f0f0 0px, #e0e0e0 40px, #f0f0f0 80px);
  background-size: 200px;
  animation: shimmer 1.5s linear infinite;
}

/* Floating elements */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.floating {
  animation: float 6s ease-in-out infinite;
}

/* Fade in animation for route transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>